import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonContainer, ContentSection, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, FormikContextType, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { setQuoteBankAccount } from '../../../../services/api/miles/miles-services';
import { updateDataStorageNew } from '../../../../services/api/updateDataStorage/updateDataStorage';
import { getTransactionId } from '../../../../services/common/data';
import { assertDefined } from '../../../../services/common/miscUtils';
import { staticSelector } from '../../../../services/redux';
import { creditApplicationSelectors } from '../../../../services/redux/features/creditApplication.slice';
import { selectDataStorage, setDataStorageNew } from '../../../../services/redux/features/dataStorage.redux';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import { BankAccountOwner, Bic, Iban, OpeningDate } from '../../utils/fieldsPrivateCustomer';
import { CustomerDetailsFormGroupForms, useFormSectionHelpers } from '../utils';
import { getPaymentDataFormInitialValues, PaymentDataFormType } from './initialValues';
import { paymentDataFormRequestMapper } from './requestMapper';
import { getPaymentDataFormValidationSchema } from './validationSchema';

interface Props {
  formName: CustomerDetailsFormGroupForms<'private'>;
}

export const PaymentDataForm: FunctionComponent<Props> = ({ formName }) => {
  const [initialValues, setInitialValues] = useState({} as PaymentDataFormType);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<PaymentDataFormType>>();

  const creditApplicantCustomerData = useSelector(
    creditApplicationSelectors.selectCreditApplicantCustomerData
  ) as CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const dataStorageData = staticSelector(selectDataStorage);
    setInitialValues(getPaymentDataFormInitialValues(creditApplicantCustomerData, dataStorageData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditApplicantCustomerData]);

  useEffect(() => {
    setValidationSchema(getPaymentDataFormValidationSchema(t));
  }, [t]);

  const { formSectionProps, onFormSubmitSuccess, onFormInitialValuesChange } = useFormSectionHelpers(
    'private',
    formName
  );

  const onSubmit = async (values: PaymentDataFormType) => {
    dispatch(loadingActions.setLoading(true));

    const { quoteId } = assertDefined(staticSelector(creditApplicationSelectors.selectCreditApplicationIds));
    setQuoteBankAccount(quoteId, paymentDataFormRequestMapper(values))
      .then(() => updateDataStorageNew(getTransactionId(), { custom: { ...values } }))
      .then((updatedDataStorageItem) => dispatch(setDataStorageNew(updatedDataStorageItem.custom)))
      .then(onFormSubmitSuccess)
      .catch(navigate.error)
      .finally(() => dispatch(loadingActions.setLoading(false)));
  };

  const isCreditApplicationDataReady = useSelector(creditApplicationSelectors.selectIsCreditApplicationDataReady);

  return (
    <ContentSection.ComponentWrapper>
      <FormSection title={t('step4:heading:paymentInformation')} {...formSectionProps}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ isValidating, isSubmitting }) =>
            isCreditApplicationDataReady && (
              /* eslint-disable-next-line no-use-before-define */
              <FormContent
                onFormInitialValuesChange={onFormInitialValuesChange}
                isValidating={isValidating}
                isSubmitting={isSubmitting}
              />
            )
          }
        </Formik>
      </FormSection>
    </ContentSection.ComponentWrapper>
  );
};

interface FormContentProps {
  onFormInitialValuesChange: (validateForm: FormikContextType<any>['validateForm']) => void;
  isValidating: boolean;
  isSubmitting: boolean;
}

const FormContent: FunctionComponent<FormContentProps> = ({
  onFormInitialValuesChange,
  isValidating,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, initialValues, validateForm, submitForm } = useFormikContext<PaymentDataFormType>();

  useEffect(() => {
    onFormInitialValuesChange(validateForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const isLoading = false;

  return (
    <Fieldset className="u-pt-none">
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/4" m="1/2" s="1/1">
            <BankAccountOwner t={t} />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/2" s="1/1">
            <OpeningDate
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              label={t('step4:openingDate:label')}
              infoText={t('step4:dateInfoText:label')}
            />
          </Layout.Item>
          <Layout.Item default="1/3" m="1/2" s="1/1">
            <Iban t={t} setFieldValue={setFieldValue} />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/2" s="1/1">
            <Bic t={t} isLoading={isLoading} />
          </Layout.Item>
          <ButtonContainer right>
            <Layout right>
              <Layout.Item default="1/3" m="2/5" s="2/3">
                <Button full type="submit" onClick={submitForm} disabled={isValidating || isSubmitting}>
                  {t('step3:navigation:continue')}
                </Button>
              </Layout.Item>
            </Layout>
          </ButtonContainer>
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  );
};
